import { makeStyles, useTheme } from "@mui/styles";
import { useEffect, useMemo, useRef, useState } from "react";
import { RTFO } from "../components/rtfo/RTFO";
import { useDispatch, useSelector } from "react-redux";
import { selectRTFO, setCurrentStageName, setPadData } from "../reducers/rtfoSlice";
import RTFOMapPadActivity from "../components/rtfo/RTFOMapPadActivity";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useSocket } from "../context/useSocket";
import { selectUser } from "../reducers/userSlice";
import { useSnackbar } from "notistack";
import { RTFOLoadingPadData } from "../components/rtfo/RTFOLoadingPadData";
import { getRTFOClientName } from "../components/rtfo/rtfoConfig";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    border: 0,
    color: 'text.primary',
    height: '100vh',
    width: 'calc(100% - 60px)',
    paddingTop: 30,
    marginLeft: 60,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
}));

const KEY_DOWN = 40;
const KEY_UP = 38;

export const RTFOContainer = ({ query }) => {
  
  const { socket } = useSocket();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const [currentWellName, setCurrentWellName] = useState('All');
  const [isLoadingData, setIsLoadingData] = useState(false);

  const rtfoMemo = useMemo(() => {
    return (
      <RTFO currentWellName={currentWellName} setCurrentWellName={setCurrentWellName} />
    );
  }, [currentWellName]);

  const padFetched = useRef(false);

  useEffect(() => {
    if (query?.padName && socket?.readyState === 1 && !currentRTFO && user?.value?.email && !isLoadingData && !padFetched.current) {
      padFetched.current = true;
      setIsLoadingData(true);
      console.log(`Fetching pad ${query.padName}`);
      socket?.send(JSON.stringify({
        messageType: 'getPadData',
        padName: query?.padName,
        initialLoad: true,
        getFullData: false,
        userEmail: user?.value?.email,
        client: getRTFOClientName(user?.value?.email)
      }));
    };
  }, []); //eslint-disable-line

  if (document.title !== ('RTFO | SSD')) document.title = ('RTFO | SSD');


  if (socket) {
    socket.onmessage = (event) => {
      const response = JSON.parse(event?.data);
  
      if (!response) return;
  
      if (response.messageType === 'padData') {
        const padData = response?.payload;
  
        if (padData) {
          
          if (padData.stages?.length > 0) {
            dispatch(setPadData({...padData, isLoadingData: true }));
            
            enqueueSnackbar(`Downloading pad ${padData?.name} with ${padData?.stages?.length || 0} stages...`, {
              variant: 'info',
              persist: true
            });
          } else {
            dispatch(setPadData(padData));
          };

          socket?.send(JSON.stringify({
            messageType: 'getSeismicityData',
            padName: query?.padName
          }));
        };      
      } else if (response.messageType === 'padAccessError') {
        enqueueSnackbar(`Your account does not have access to pad ${response?.padName}`, {
          variant: 'error',
          persist: false
        });
      };
      setIsLoadingData(false);

    };
  };

  return (
    <div 
      className={classes.root}
      onKeyDown={handleKeyDown}
      tabIndex="0"
      style={{backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light']}}
    > 
      {currentRTFO ? 
        rtfoMemo : 
        isLoadingData ? 
        <RTFOLoadingPadData darkMode={darkMode} />
        : 
        <RTFOMapPadActivity />
      }
    </div>
  );

  function handleKeyDown(event) {
    try {

      if (currentRTFO?.isLoadingData) return; // prevent stage switching while data is loading

      let stageNames = currentRTFO?.stageData ? Object.keys(currentRTFO?.stageData) : [];
      if (currentWellName !== 'All') {
        stageNames = stageNames.filter(name => name.includes(currentWellName));
      };
      stageNames?.sort((a, b) => currentRTFO?.stageData?.[b]?.stageStartTimestamp - currentRTFO?.stageData?.[a]?.stageStartTimestamp);

      if (stageNames.length === 0) return;

      const currentStageIndex = stageNames.indexOf(currentRTFO?.currentStageName);

      let newStageName = null;

      if (event.keyCode === KEY_DOWN) {
        newStageName = stageNames[currentStageIndex + 1] || null;
      } else if (event.keyCode === KEY_UP) {
        newStageName = stageNames[currentStageIndex - 1] || null;
      };


      if (newStageName) {
        dispatch(setCurrentStageName(newStageName));
      };
      
    } catch (error) {
      console.log(error);
    };
    
  };
};

export function RTFOContainerLoader({ darkMode }) {
  const query = useParams();

  const [isSocketReady, setIsSocketReady] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsSocketReady(true);
    }, 1000);
  });

  if (!isSocketReady) {
    return (
      <RTFOLoadingPadData darkMode={darkMode} />
    );
  } else {
    return <RTFOContainer query={query}/>;
  };
};
